import React, { useEffect, useMemo, useRef, useState, type FC } from 'react';
import {
  Box,
  Button,
  CardContent,
  CardHeader,
  IconButton,
  InputAdornment,
  MenuItem,
  SvgIcon,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers';
import PlusIcon from '@untitled-ui/icons-react/build/esm/Plus';
import { TrashIcon } from 'src/components/therapyPlanning_2.0/commonComponent/therapy-planning-icons';
import moment from 'moment';
import { useSelector } from 'react-redux';

// Interface for Prior Charge
export interface PriorCharge {
  id: string;
  year: number | '';
  fromDate: Date | null;
  toDate: Date | null;
  ptSlpCharges: string;
  otCharges: string;
}

export interface PriorMedicareChargesRef {
  validate: () => { isValid: boolean; errors: any };
  getFormattedCharges: () => any[];
  getPriorCharges: () => PriorCharge[];
}

interface PriorMedicareChargesProps {
  action: 'Create' | 'Edit';
  patientData?: any;
  formik: any;
  showValidationErrors: boolean;
}

// Helper function to format date to UTC without time
const getCurrentDateUTCWithoutTime = (date: any) => {
  const utcDateFormate = moment(date).format('YYYY-MM-DD');
  const utcDateString = `${utcDateFormate}T00:00:00.000Z`;
  return utcDateString;
};

// Helper function to generate year options (2024 to current year)
const getYearOptions = () => {
  const currentYear = new Date().getFullYear();
  const years = [];
  for (let year = 2024; year <= currentYear; year++) {
    years.push(year);
  }
  return years;
};

// Helper function to check if dates overlap
const datesOverlap = (
  fromDate1: Date | null,
  toDate1: Date | null,
  fromDate2: Date | null,
  toDate2: Date | null
): boolean => {
  if (!fromDate1 || !toDate1 || !fromDate2 || !toDate2) return false;
  return fromDate1 <= toDate2 && fromDate2 <= toDate1;
};

// Helper function to check if date is within year
const isDateInYear = (date: Date | null, year: number | ''): boolean => {
  if (!date || !year) return false;
  return date.getFullYear() === year;
};

// Generate unique ID for prior charge rows
const generatePriorChargeId = () =>
  `prior-charge-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

// Prior Charge Row Component
interface PriorChargeRowProps {
  charge: PriorCharge;
  index: number;
  chargeErrors: any;
  isNewPatient: boolean;
  yearOptions: number[];
  priorCharges: PriorCharge[];
  setPriorCharges: React.Dispatch<React.SetStateAction<PriorCharge[]>>;
  showValidationErrors: boolean;
  formik: any;
  onUserModification: () => void; // Callback to mark charges as user-modified
  onDeleteCharge: (chargeId: string) => void; // Callback to remove charge from API tracking
}

const PriorChargeRow: FC<PriorChargeRowProps> = ({
  charge,
  index,
  chargeErrors,
  isNewPatient,
  yearOptions,
  priorCharges,
  setPriorCharges,
  showValidationErrors,
  formik,
  onUserModification,
  onDeleteCharge,
}) => {
  // Calculate min and max dates based on selected year
  const getYearDateRange = (year: number | '') => {
    if (!year || typeof year !== 'number') {
      return { minDate: undefined, maxDate: undefined };
    }
    const startOfYear = new Date(year, 0, 1); // January 1st
    const endOfYear = new Date(year, 11, 31); // December 31st
    return { minDate: startOfYear, maxDate: endOfYear };
  };

  const { minDate: yearMinDate, maxDate: yearMaxDate } = getYearDateRange(charge.year);

  const handleYearChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onUserModification(); 
    const updatedCharges = [...priorCharges];
    const newYear = Number(e.target.value);
    updatedCharges[index] = { ...charge, year: newYear };

    // If dates exist and are outside the new year, clear them
    const { minDate: newMinDate, maxDate: newMaxDate } = getYearDateRange(newYear);
    let updatedCharge = { ...charge, year: newYear };

    if (charge.fromDate) {
      const fromDateYear = charge.fromDate.getFullYear();
      if (fromDateYear !== newYear) {
        updatedCharge.fromDate = null;
        formik.setFieldValue(`priorCharges[${index}].fromDate`, null, false);
      }
    }

    if (charge.toDate) {
      const toDateYear = charge.toDate.getFullYear();
      if (toDateYear !== newYear) {
        updatedCharge.toDate = null;
        formik.setFieldValue(`priorCharges[${index}].toDate`, null, false);
      }
    }

    updatedCharges[index] = updatedCharge;
    setPriorCharges(updatedCharges);
    // Update formik value immediately
    formik.setFieldValue(`priorCharges[${index}].year`, newYear, false);
    // Trigger validation for year and related date fields
    setTimeout(() => {
      formik.validateField(`priorCharges[${index}].year`);
      if (updatedCharge.fromDate) {
        formik.validateField(`priorCharges[${index}].fromDate`);
      } else {
        // Clear fromDate error if it was cleared
        formik.setFieldError(`priorCharges[${index}].fromDate`, undefined);
      }
      if (updatedCharge.toDate) {
        formik.validateField(`priorCharges[${index}].toDate`);
      } else {
        // Clear toDate error if it was cleared
        formik.setFieldError(`priorCharges[${index}].toDate`, undefined);
      }
    }, 150);
  };

  const handleFromDateChange = (newDate: Date | null) => {
    onUserModification();
    const updatedCharges = [...priorCharges];
    updatedCharges[index] = { ...charge, fromDate: newDate };
    setPriorCharges(updatedCharges);
    // Update formik value immediately
    formik.setFieldValue(`priorCharges[${index}].fromDate`, newDate, false);
    // Trigger validation for this field and related fields after a short delay
    setTimeout(() => {
      formik.validateField(`priorCharges[${index}].fromDate`);
      // Also validate toDate if it exists, as it depends on fromDate
      if (updatedCharges[index].toDate) {
        formik.validateField(`priorCharges[${index}].toDate`);
      }
    }, 150);
  };

  const handleToDateChange = (newDate: Date | null) => {
    onUserModification();
    const updatedCharges = [...priorCharges];
    updatedCharges[index] = { ...charge, toDate: newDate };
    setPriorCharges(updatedCharges);
    // Update formik value immediately
    formik.setFieldValue(`priorCharges[${index}].toDate`, newDate, false);
    // Trigger validation for this field after a short delay
    setTimeout(() => {
      formik.validateField(`priorCharges[${index}].toDate`);
    }, 150);
  };

  const handlePtSlpChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (/^[0-9]*\.?[0-9]*$/.test(e.target.value)) {
      onUserModification();
      const updatedCharges = [...priorCharges];
      updatedCharges[index] = { ...charge, ptSlpCharges: e.target.value };
      setPriorCharges(updatedCharges);
      // Update formik value immediately
      formik.setFieldValue(`priorCharges[${index}].ptSlpCharges`, e.target.value, false);
      // Trigger validation for this field and OT field (for cross-field validation) after a short delay
      setTimeout(() => {
        formik.validateField(`priorCharges[${index}].ptSlpCharges`);
        // Always validate OT field for cross-field validation
        formik.validateField(`priorCharges[${index}].otCharges`);
      }, 300);
    }
  };

  const handleOtChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (/^[0-9]*\.?[0-9]*$/.test(e.target.value)) {
      onUserModification();
      const updatedCharges = [...priorCharges];
      updatedCharges[index] = { ...charge, otCharges: e.target.value };
      setPriorCharges(updatedCharges);
      // Update formik value immediately
      formik.setFieldValue(`priorCharges[${index}].otCharges`, e.target.value, false);
      // Trigger validation for this field and PT/SLP field (for cross-field validation) after a short delay
      setTimeout(() => {
        formik.validateField(`priorCharges[${index}].otCharges`);
        // Always validate PT/SLP field for cross-field validation
        formik.validateField(`priorCharges[${index}].ptSlpCharges`);
      }, 300);
    }
  };

  const handleDelete = () => {
    onUserModification();
    onDeleteCharge(charge.id);
    const updatedCharges = priorCharges.filter((_charge: PriorCharge, i: number) => i !== index);
    setPriorCharges(updatedCharges);
    formik.setFieldValue('priorCharges', updatedCharges, false);
  };

  return (
    <TableRow key={charge.id}>
      <TableCell sx={{ verticalAlign: 'middle', padding: '16px' }}>
        <TextField
          select
          fullWidth
          value={charge.year || ''}
          onChange={handleYearChange}
          onBlur={() => {
            formik.validateField(`priorCharges[${index}].year`);
          }}
          error={!!chargeErrors.year || (showValidationErrors && !charge.year)}
          helperText={chargeErrors.year || ''}
          name={`priorCharges[${index}].year`}
          label="Year"
        >
          {yearOptions.map((year) => (
            <MenuItem
              key={year}
              value={year}
            >
              {year}
            </MenuItem>
          ))}
        </TextField>
      </TableCell>
      <TableCell sx={{ verticalAlign: 'middle', padding: '16px' }}>
        <DatePicker
          value={charge.fromDate}
          onChange={handleFromDateChange}
          onClose={() => { }}
          format="MMM dd, yyyy"
          label="From Date *"
          minDate={yearMinDate}
          maxDate={yearMaxDate}
          slotProps={{
            textField: {
              fullWidth: true,
              name: `priorCharges[${index}].fromDate`,
              error: !!(chargeErrors.fromDate || chargeErrors.dateOverlap) || (showValidationErrors && !charge.fromDate),
              helperText: (chargeErrors.fromDate || chargeErrors.dateOverlap) || (showValidationErrors && !charge.fromDate ? 'From Date is required' : ''),
              onBlur: () => {
                formik.validateField(`priorCharges[${index}].fromDate`);
              },
            },
          }}
          sx={{ width: '100%' }}
          disableFuture
        />
      </TableCell>
      <TableCell sx={{ verticalAlign: 'middle', padding: '16px' }}>
        <DatePicker
          value={charge.toDate}
          onChange={handleToDateChange}
          onClose={() => { }}
          format="MMM dd, yyyy"
          label="To Date *"
          minDate={charge.fromDate || yearMinDate}
          maxDate={yearMaxDate}
          slotProps={{
            textField: {
              fullWidth: true,
              name: `priorCharges[${index}].toDate`,
              error: !!(chargeErrors.toDate || chargeErrors.dateOverlap) || (showValidationErrors && !charge.toDate),
              helperText: (chargeErrors.toDate || chargeErrors.dateOverlap) || (showValidationErrors && !charge.toDate ? 'To Date is required' : ''),
              onBlur: () => {
                formik.validateField(`priorCharges[${index}].toDate`);
              },
            },
          }}
          sx={{ width: '100%' }}
          disableFuture
        />
      </TableCell>
      <TableCell sx={{ verticalAlign: 'middle', padding: '16px' }}>
        <TextField
          fullWidth
          value={charge.ptSlpCharges ?? '0'}
          onChange={handlePtSlpChange}
          onBlur={() => {
            formik.validateField(`priorCharges[${index}].ptSlpCharges`);
            // Also validate OT when PT/SLP changes to check cross-field validation
            formik.validateField(`priorCharges[${index}].otCharges`);
          }}
          error={!!chargeErrors.ptSlpCharges}
          helperText={chargeErrors.ptSlpCharges || ''}
          placeholder="0"
          name={`priorCharges[${index}].ptSlpCharges`}
          label="PT/SLP *"
          InputProps={{
            startAdornment: <InputAdornment position="start">$</InputAdornment>,
          }}
        />
      </TableCell>
      <TableCell sx={{ verticalAlign: 'middle', padding: '16px' }}>
        <TextField
          fullWidth
          value={charge.otCharges ?? '0'}
          onChange={handleOtChange}
          onBlur={() => {
            formik.validateField(`priorCharges[${index}].otCharges`);
            // Also validate PT/SLP when OT changes to check cross-field validation
            formik.validateField(`priorCharges[${index}].ptSlpCharges`);
          }}
          error={!!chargeErrors.otCharges}
          helperText={chargeErrors.otCharges || ''}
          placeholder="0"
          name={`priorCharges[${index}].otCharges`}
          label="OT *"
          InputProps={{
            startAdornment: <InputAdornment position="start">$</InputAdornment>,
          }}
        />
      </TableCell>
      <TableCell
        align="right"
        sx={{ verticalAlign: 'middle', padding: '16px' }}
      >
        <IconButton
          onClick={handleDelete}
          size="small"
          title="Delete"
        >
          <TrashIcon />
        </IconButton>
      </TableCell>
    </TableRow>
  );
};

export const PriorMedicareCharges = React.forwardRef<
  PriorMedicareChargesRef,
  PriorMedicareChargesProps
>(({ action, patientData, formik, showValidationErrors }, ref) => {
  const [priorCharges, setPriorCharges] = useState<PriorCharge[]>([]);
  const priorChargesInitialized = useRef(false);
  const userHasModifiedCharges = useRef(false); // Track if user has explicitly modified charges
  const priorChargesRef = useRef<PriorCharge[]>([]); // Ref to track current charges for async updates
  const apiChargeIds = useRef<Set<string>>(new Set()); // Track which charge IDs came from API
  const yearOptions = useMemo(() => getYearOptions(), []);
  const isNewPatient = action === 'Create';
  const getAllPatientPriorChargesData = useSelector((state: any) => state?.getAllPatientPriorChargesReducer);

  const displayStartandToDateWithoutTimeZone = (date: any) => {
    const dateWithoutTimeZone = date?.split('T')?.at(0);
    return new Date(
      new Date(dateWithoutTimeZone).valueOf() +
      new Date(dateWithoutTimeZone).getTimezoneOffset() * 60 * 1000
    )
  };

  // Initialize and update priorCharges from API data (for Edit mode) or patientData
  // Handles both initial load and async API data updates
  useEffect(() => {
    // Only process if we're in Edit mode
    if (action !== 'Edit') {
      return;
    }

    // Check if API data is available and valid
    const hasApiData =
      getAllPatientPriorChargesData &&
      !getAllPatientPriorChargesData.isError &&
      getAllPatientPriorChargesData.priorCharges &&
      Array.isArray(getAllPatientPriorChargesData.priorCharges) &&
      getAllPatientPriorChargesData.priorCharges.length > 0 &&
      !getAllPatientPriorChargesData.loading;

    // Initial load: Use API data or patientData
    if (!priorChargesInitialized.current) {
      let initialCharges: PriorCharge[] = [];

      // Priority 1: Use API data from getAllPatientPriorChargesData
      if (hasApiData) {
        initialCharges = getAllPatientPriorChargesData.priorCharges.map((charge: any) => {
          const chargeId = charge.id || generatePriorChargeId();
          apiChargeIds.current.add(chargeId); // Track this as API-sourced charge
          return {
            id: chargeId,
            year: charge.year || new Date().getFullYear(),
            fromDate: charge.fromDate ? displayStartandToDateWithoutTimeZone(charge.fromDate) : null,
            toDate: charge.toDate ? displayStartandToDateWithoutTimeZone(charge.toDate) : null,
            ptSlpCharges: charge.ptSlpCharges?.toString() || '0',
            otCharges: charge.otCharges?.toString() || '0',
          };
        });
      }
      // Priority 2: Use patientData priorCharges array (if API data not available)
      else if (patientData) {
        // If patient has priorCharges array, use it
        if (
          patientData?.priorCharges &&
          Array.isArray(patientData.priorCharges) &&
          patientData.priorCharges.length > 0
        ) {
          initialCharges = patientData.priorCharges.map((charge: any) => {
            const chargeId = charge.id || generatePriorChargeId();
            apiChargeIds.current.add(chargeId); // Track this as API-sourced charge
            return {
              id: chargeId,
              year: charge.year || new Date().getFullYear(),
              fromDate: charge.fromDate ? displayStartandToDateWithoutTimeZone(charge.fromDate) : null,
              toDate: charge.toDate ? displayStartandToDateWithoutTimeZone(charge.toDate) : null,
              ptSlpCharges: charge.ptSlpCharges?.toString() || '0',
              otCharges: charge.otCharges?.toString() || '0',
            };
          });
        } else {
          // Migrate old format (ptSlpCharges/otCharges) to new format
          const hasOldCharges =
            (patientData?.ptSlpCharges !== undefined &&
              patientData?.ptSlpCharges !== null &&
              patientData?.ptSlpCharges !== '') ||
            (patientData?.otCharges !== undefined &&
              patientData?.otCharges !== null &&
              patientData?.otCharges !== '');

          if (hasOldCharges) {
            initialCharges = [
              {
                id: generatePriorChargeId(),
                year: new Date().getFullYear(), // Assume existing charges are for current year
                fromDate: null, // Dates optional for existing patients
                toDate: null,
                ptSlpCharges:
                  patientData?.ptSlpCharges === 0 || patientData?.ptSlpCharges
                    ? patientData.ptSlpCharges.toString()
                    : '0',
                otCharges:
                  patientData?.otCharges === 0 || patientData?.otCharges
                    ? patientData.otCharges.toString()
                    : '0',
              },
            ];
          }
        }
      }

      // For edit patients without charges, start with empty array
      if (initialCharges.length === 0) {
        initialCharges = [];
      }

      setPriorCharges(initialCharges);
      priorChargesRef.current = initialCharges; // Update ref
      formik.setFieldValue('priorCharges', initialCharges, false);
      priorChargesInitialized.current = true;
    }
    // Handle async API data update after initial load
    // This handles the case where API data arrives after component initialization
    else if (hasApiData) {
      // Check if current charges are empty or just default values (use ref to avoid dependency)
      const currentCharges = priorChargesRef.current;
      
      // Improved logic: A charge is "default" only if:
      // 1. It has no dates AND zero charges AND was NOT sourced from API
      // 2. OR the array is completely empty
      const hasOnlyDefaultCharges = (() => {
        if (currentCharges.length === 0) return true;
        
        // Check if all charges are truly defaults (not from API)
        const allAreDefaults = currentCharges.every((charge) => {
          // If charge came from API, it's NOT a default (even with zero values)
          if (apiChargeIds.current.has(charge.id)) return false;
          
          // A charge is default only if it has no dates AND zero charges
          return (
            charge.fromDate === null &&
            charge.toDate === null &&
            (charge.ptSlpCharges === '0' || charge.ptSlpCharges === '') &&
            (charge.otCharges === '0' || charge.otCharges === '')
          );
        });
        
        return allAreDefaults;
      })();

      // Only update if we have default/empty charges AND user hasn't explicitly modified them
      // This prevents repopulating charges when user has deleted all rows or modified API data
      if (hasOnlyDefaultCharges && !userHasModifiedCharges.current) {
        const updatedCharges = getAllPatientPriorChargesData.priorCharges.map((charge: any) => {
          const chargeId = charge.id || generatePriorChargeId();
          apiChargeIds.current.add(chargeId); // Track this as API-sourced charge
          return {
            id: chargeId,
            year: charge.year || new Date().getFullYear(),
            fromDate: charge.fromDate ? displayStartandToDateWithoutTimeZone(charge.fromDate) : null,
            toDate: charge.toDate ? displayStartandToDateWithoutTimeZone(charge.toDate) : null,
            ptSlpCharges: charge.ptSlpCharges?.toString() || '0',
            otCharges: charge.otCharges?.toString() || '0',
          };
        });

        setPriorCharges(updatedCharges);
        priorChargesRef.current = updatedCharges; // Update ref
        formik.setFieldValue('priorCharges', updatedCharges, false);
      }
    }
  }, [
    action,
    patientData,
    formik,
    getAllPatientPriorChargesData?.priorCharges,
    getAllPatientPriorChargesData?.isError,
    getAllPatientPriorChargesData?.loading,
  ]);

  // Sync priorCharges state with formik values (without triggering validation on every change)
  useEffect(() => {
    if (priorChargesInitialized.current) {
      priorChargesRef.current = priorCharges; // Update ref whenever priorCharges changes
      formik.setFieldValue('priorCharges', priorCharges, false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [priorCharges]);

  // Expose methods to parent via ref
  React.useImperativeHandle(
    ref,
    () => ({
      validate: validatePriorCharges,
      getFormattedCharges: getFormattedPriorCharges,
      getPriorCharges: () => priorCharges,
    }),
    [priorCharges, isNewPatient]
  );

  const handleAddRow = () => {
    userHasModifiedCharges.current = true; // Mark as user-modified
    const currentYear = new Date().getFullYear();
    const newCharge: PriorCharge = {
      id: generatePriorChargeId(),
      year: currentYear,
      fromDate: null,
      toDate: null,
      ptSlpCharges: '0',
      otCharges: '0',
    };
    const updatedCharges = [...priorCharges, newCharge];
    setPriorCharges(updatedCharges);
    // Immediately sync to formik to ensure year is available for validation
    const currentFormikCharges = formik.values.priorCharges || [];
    const updatedFormikCharges = [...currentFormikCharges, {
      year: currentYear,
      fromDate: null,
      toDate: null,
      ptSlpCharges: '0',
      otCharges: '0',
    }];
    formik.setFieldValue('priorCharges', updatedFormikCharges, false);
  };

  // Validation function that can be called from parent
  const validatePriorCharges = (): { isValid: boolean; errors: any } => {
    const errors: any = {};

    // For new patients, empty array is allowed (no validation required)
    // For edit patients, empty array is also allowed (they may not have prior charges)
    // Only validate if there are entries
    if (priorCharges.length === 0) {
      return {
        isValid: true,
        errors: {},
      };
    }

    priorCharges.forEach((charge: PriorCharge, index: number) => {
      const chargeErrors: any = {};

      // Validate Year - required for all
      if (charge.year === '' || typeof charge.year !== 'number') {
        chargeErrors.year = 'Year is required';
      }

      // Validate From Date - required for all
      if (!charge.fromDate) {
        chargeErrors.fromDate = 'From Date is required';
      }

      // Validate To Date - required for all
      if (!charge.toDate) {
        chargeErrors.toDate = 'To Date is required';
      }

      // Validate PT/SLP - if a value is provided, it must be a valid number >= 0
      if (charge.ptSlpCharges && charge.ptSlpCharges !== '') {
        const ptSlpValue = parseFloat(charge.ptSlpCharges);
        if (isNaN(ptSlpValue) || ptSlpValue < 0) {
          chargeErrors.ptSlpCharges = 'PT/SLP must be a valid number';
        }
      }

      // Validate OT - if a value is provided, it must be a valid number >= 0
      if (charge.otCharges && charge.otCharges !== '') {
        const otValue = parseFloat(charge.otCharges);
        if (isNaN(otValue) || otValue < 0) {
          chargeErrors.otCharges = 'OT must be a valid number';
        }
      }

      // Validate toDate >= fromDate (only if both dates exist)
      if (charge.fromDate && charge.toDate) {
        // Reset time to compare only dates
        const fromDateOnly = new Date(charge.fromDate);
        fromDateOnly.setHours(0, 0, 0, 0);
        const toDateOnly = new Date(charge.toDate);
        toDateOnly.setHours(0, 0, 0, 0);

        if (toDateOnly < fromDateOnly) {
          chargeErrors.toDate = 'To Date must be greater than or equal to From Date';
        }
      }

      // Validate dates are within selected year (only if year and dates exist)
      if (charge.year && charge.fromDate && charge.toDate) {
        if (!isDateInYear(charge.fromDate, charge.year)) {
          chargeErrors.fromDate = `From Date must be within ${charge.year}`;
        }
        if (!isDateInYear(charge.toDate, charge.year)) {
          chargeErrors.toDate = `To Date must be within ${charge.year}`;
        }
      }

      // Check for overlaps with other rows (only if both dates exist for current row)
      if (charge.fromDate && charge.toDate) {
        priorCharges.forEach((otherCharge: PriorCharge, otherIndex: number) => {
          if (
            index !== otherIndex &&
            otherCharge.fromDate &&
            otherCharge.toDate
          ) {
            if (
              datesOverlap(charge.fromDate, charge.toDate, otherCharge.fromDate, otherCharge.toDate)
            ) {
              chargeErrors.dateOverlap = 'Date range overlaps with another entry';
            }
          }
        });
      }

      if (Object.keys(chargeErrors).length > 0) {
        errors[index] = chargeErrors;
      }
    });

    return {
      isValid: Object.keys(errors).length === 0,
      errors,
    };
  };

  // Format priorCharges for API submission
  const getFormattedPriorCharges = () => {
    return priorCharges.map((charge: PriorCharge) => ({
      year: charge.year,
      fromDate: charge.fromDate ? getCurrentDateUTCWithoutTime(charge.fromDate) : null,
      toDate: charge.toDate ? getCurrentDateUTCWithoutTime(charge.toDate) : null,
      ptSlpCharges: charge.ptSlpCharges ? parseFloat(charge.ptSlpCharges) : 0,
      otCharges: charge.otCharges ? parseFloat(charge.otCharges) : 0,
    }));
  };

  // Get errors from formik - Yup validation errors will be structured as an array
  // Memoize error processing to avoid recalculating on every render
  const chargeErrors = useMemo(() => {
    const priorChargesErrors = formik.errors.priorCharges as any;
    const errors: any = {};

    // Convert Yup array errors to index-based object format
    if (Array.isArray(priorChargesErrors)) {
      priorChargesErrors.forEach((error: any, index: number) => {
        if (error && typeof error === 'object') {
          // Yup errors are objects with field names as keys (e.g., { year: 'Year is required', fromDate: '...' })
          // Merge with existing errors if any
          errors[index] = { ...errors[index], ...error };
        } else if (error) {
          // Handle string errors
          errors[index] = { ...errors[index], general: error };
        }
      });
    } else if (priorChargesErrors && typeof priorChargesErrors === 'object') {
      // If it's already an object (from custom validation or overlap errors), use it as is
      Object.keys(priorChargesErrors).forEach((key) => {
        const index = parseInt(key, 10);
        if (!isNaN(index)) {
          // Merge with existing errors
          errors[index] = { ...errors[index], ...priorChargesErrors[key] };
        } else if (key !== 'general') {
          // Handle non-numeric keys
          errors[key] = priorChargesErrors[key];
        }
      });
    }

    return errors;
  }, [formik.errors.priorCharges, showValidationErrors]);

  const hasCharges = Array.isArray(priorCharges) && priorCharges.length > 0;

  return (
    <>
      <CardHeader title="Prior Medicare B Charges" />
      <CardContent sx={{ py: 0 }}>
        <Box sx={{ padding: '20px 0px' }}>
          {hasCharges ? (
            <Table sx={{ minWidth: 700, mb: 2 }}>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ padding: '16px' }}>Year</TableCell>
                  <TableCell sx={{ padding: '16px' }}>From Date</TableCell>
                  <TableCell sx={{ padding: '16px' }}>To Date</TableCell>
                  <TableCell sx={{ padding: '16px' }}>PT/SLP</TableCell>
                  <TableCell sx={{ padding: '16px' }}>OT</TableCell>
                  <TableCell
                    align="right"
                    sx={{ padding: '16px' }}
                  ></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {priorCharges.map((charge: PriorCharge, index: number) => {
                  const rowErrors = chargeErrors[index] || {};
                  return (
                    <PriorChargeRow
                      key={charge.id}
                      charge={charge}
                      index={index}
                      chargeErrors={rowErrors}
                      isNewPatient={isNewPatient}
                      yearOptions={yearOptions}
                      priorCharges={priorCharges}
                      setPriorCharges={setPriorCharges}
                      showValidationErrors={showValidationErrors}
                      formik={formik}
                      onUserModification={() => {
                        userHasModifiedCharges.current = true;
                      }}
                      onDeleteCharge={(chargeId: string) => {
                        apiChargeIds.current.delete(chargeId);
                      }}
                    />
                  );
                })}
              </TableBody>
            </Table>
          ) : (
            <Table sx={{ minWidth: 700, mb: 2 }}>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ padding: '16px' }}>Year</TableCell>
                  <TableCell sx={{ padding: '16px' }}>From Date</TableCell>
                  <TableCell sx={{ padding: '16px' }}>To Date</TableCell>
                  <TableCell sx={{ padding: '16px' }}>PT/SLP</TableCell>
                  <TableCell sx={{ padding: '16px' }}>OT</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                <TableRow>
                  <TableCell colSpan={5}>
                    <Box
                      textAlign="center"
                      p={4}
                    >
                      <Typography
                        color={'text.secondary'}
                        variant="subtitle1"
                      >
                        No prior charges found for this patient.
                      </Typography>
                    </Box>
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          )}
          <Button
            type="button"
            startIcon={
              <SvgIcon>
                <PlusIcon />
              </SvgIcon>
            }
            variant="outlined"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              handleAddRow();
            }}
            sx={{ mt: 1 }}
          >
            Add Prior Charges
          </Button>
        </Box>
      </CardContent>
    </>
  );
});
PriorMedicareCharges.displayName = 'PriorMedicareCharges';

// Export validation and formatting utilities for use in parent component
export const validatePriorChargesData = (
  priorCharges: PriorCharge[],
  isNewPatient: boolean
): { isValid: boolean; errors: any } => {
  const errors: any = {};

  // For new patients, empty array is allowed (no prior charges required)
  // For edit patients, empty array is also allowed (they may not have prior charges)
  // Only validate if there are entries
  if (priorCharges.length === 0) {
    return {
      isValid: true,
      errors: {},
    };
  }

  priorCharges.forEach((charge: PriorCharge, index: number) => {
    const chargeErrors: any = {};

    // For new patients, dates are required
    if (isNewPatient) {
      if (!charge.fromDate) {
        chargeErrors.fromDate = 'From Date is required for new patients';
      }
      if (!charge.toDate) {
        chargeErrors.toDate = 'To Date is required for new patients';
      }
    }

    // Validate toDate >= fromDate (only if both dates exist)
    if (charge.fromDate && charge.toDate) {
      // Reset time to compare only dates
      const fromDateOnly = new Date(charge.fromDate);
      fromDateOnly.setHours(0, 0, 0, 0);
      const toDateOnly = new Date(charge.toDate);
      toDateOnly.setHours(0, 0, 0, 0);

      if (toDateOnly < fromDateOnly) {
        chargeErrors.toDate = 'To Date must be greater than or equal to From Date';
      }
    }

    // Validate dates are within selected year (only if year and dates exist)
    if (charge.year) {
      if (charge.fromDate && !isDateInYear(charge.fromDate, charge.year)) {
        chargeErrors.fromDate = `From Date must be within ${charge.year}`;
      }
      if (charge.toDate && !isDateInYear(charge.toDate, charge.year)) {
        chargeErrors.toDate = `To Date must be within ${charge.year}`;
      }
    }

    // Check for overlaps with other rows (only if both dates exist for current row)
    if (charge.fromDate && charge.toDate) {
      priorCharges.forEach((otherCharge: PriorCharge, otherIndex: number) => {
        if (
          index !== otherIndex &&
          otherCharge.fromDate &&
          otherCharge.toDate
        ) {
          if (
            datesOverlap(charge.fromDate, charge.toDate, otherCharge.fromDate, otherCharge.toDate)
          ) {
            chargeErrors.dateOverlap = 'Date range overlaps with another entry';
          }
        }
      });
    }

    if (Object.keys(chargeErrors).length > 0) {
      errors[index] = chargeErrors;
    }
  });

  return {
    isValid: Object.keys(errors).length === 0,
    errors,
  };
};

export const formatPriorChargesForAPI = (priorCharges: PriorCharge[]) => {
  return priorCharges.map((charge: PriorCharge) => ({
    year: charge.year,
    fromDate: charge.fromDate ? getCurrentDateUTCWithoutTime(charge.fromDate) : null,
    toDate: charge.toDate ? getCurrentDateUTCWithoutTime(charge.toDate) : null,
    ptSlpCharges: charge.ptSlpCharges ? parseFloat(charge.ptSlpCharges) : 0,
    otCharges: charge.otCharges ? parseFloat(charge.otCharges) : 0,
  }));
};
